import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { AiFillAppstore } from "react-icons/ai";
import { deleteAlert, errorAlert } from "../../Components/Alerts";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import ReusableServerDataGrid from "../../Components/ReusableServerDataGrid";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import { AddElementsButton } from "../../Components/AddElementsButton";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { formatDate } from "../../Components/formatDate";
import CreditNoteViewModal from "./CreditNoteViewModal";
import CreditNoteGridHeader from "./CreditNoteGridHeader";
import "./CreditNotePage.scss";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function CreditNotePage() {
  const history = useHistory();
  const { fiscalYear } = useContext(FiscalYearContext);
  const headers = authHeader();

  const [data, setData] = useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 50,
  });
  const [sortModel, setSortModel] = React.useState([]);
  const [filterModel, setFilterModel] = React.useState({ items: [] });
  const [quickFilter, setQuickFilter] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [totals, setTotals] = useState({
    AmountWithoutVAT: 0,
    VATTotal: 0,
    Total: 0,
  });

  const fetchTotals = async (fiscal, activeFilterItems = [], activeQuickFilter = []) => {
    try {
      const response = await axios.get(ApiLink.API_URL + "CreditNote/getTotals", {
        headers,
        params: {
          fiscalYear: fiscal,
          filter: JSON.stringify(activeFilterItems || []),
          quickFilter: (activeQuickFilter || []).join(" "),
        },
      });
      setTotals(response.data);
    } catch (error) {
      errorAlert("Gabim në marrjen e totaleve të credit notes.");
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await axios.get(`${ApiLink.API_URL}CreditNote/getAllCreditNoteData`, {
        headers,
        params: {
          fiscalYear,
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sort: JSON.stringify(sortModel),
          filter: JSON.stringify(filterModel.items),
          quickFilter: quickFilter.join(" "),
        },
      });

      const transformedData = (response.data.data || []).map((item) => ({
        ...item,
        ...item.Account,
      }));

      setData(transformedData);
      setRowCount(response.data.totalRows || 0);
    } catch (error) {
      errorAlert("Gabim në marrjen e listës së credit notave.");
    } finally {
      setLoading(false);
    }
  };

  const fetchExportRows = async () => {
    const totalRowsToExport = Math.max(Number(rowCount) || 0, 1);

    const response = await axios.get(`${ApiLink.API_URL}CreditNote/getAllCreditNoteData`, {
      headers,
      params: {
        fiscalYear,
        page: 0,
        pageSize: totalRowsToExport,
        sort: JSON.stringify(sortModel),
        filter: JSON.stringify(filterModel.items),
        quickFilter: quickFilter.join(" "),
      },
    });

    const items = response?.data?.data || [];
    return items.map((item, index) => ({
      ...item,
      ...item.Account,
      rowNumber: index + 1,
      InvoiceNr:
        item.FullInvoiceNr ||
        (item.InvoiceNr ? `CNT-${item.FiscalYear || fiscalYear}-${item.InvoiceNr}` : ""),
      Date: formatDate(item.Date),
      AccountName: item.AccountName,
      AmountWithoutVAT: item.AmountWithoutVAT,
      VATTotal: item.VATTotal,
      Total: item.Total,
      rowInfo: item,
    }));
  };

  const handleDelete = async (deleteItemId) => {
    try {
      await axios.delete(ApiLink.API_URL + "CreditNote/delete-creditnote" + `/${deleteItemId}`, {
        headers,
      });
      deleteAlert("Credit nota është fshirë me sukses");
      fetchData();
      fetchTotals(fiscalYear, filterModel.items, quickFilter);
    } catch (error) {
      deleteAlert("Credit nota nuk është fshirë me sukses");
    }
  };

  const handleEdit = (item) => {
    history.push(`/Sales/creditnote/edit/${item.TransactionID}`);
  };

  const handleView = async (item) => {
    setShowViewModal(true);
    setViewLoading(true);

    try {
      const response = await axios.get(
        `${ApiLink.API_URL}CreditNote/view-mode/${item.TransactionID}`,
        { headers },
      );

      const payload = response?.data || {};
      setViewData({
        ...payload,
        InvoiceNr: payload.InvoiceNr ?? payload.invoiceNr,
        Date: payload.Date ?? payload.date,
        AccountName: payload.AccountName ?? payload.accountName,
        PaymentOptions: payload.PaymentOptions ?? payload.paymentOptions,
        PaymentTypeName: payload.PaymentTypeName ?? payload.paymentTypeName,
        TransactionType: payload.TransactionType ?? payload.transactionType,
        Coment: payload.Coment ?? payload.coment,
        Amount0: payload.Amount0 ?? payload.amount0,
        Amount8: payload.Amount8 ?? payload.amount8,
        Amount18: payload.Amount18 ?? payload.amount18,
        VAT8: payload.VAT8 ?? payload.vat8,
        VAT18: payload.VAT18 ?? payload.vat18,
        Total0: payload.Total0 ?? payload.total0,
        Total8: payload.Total8 ?? payload.total8,
        Total18: payload.Total18 ?? payload.total18,
        AmountWithoutVAT: payload.AmountWithoutVAT ?? payload.amountWithoutVAT,
        VATTotal: payload.VATTotal ?? payload.vatTotal,
        Total: payload.Total ?? payload.total,
      });
    } catch (error) {
      setShowViewModal(false);
      errorAlert("Gabim në marrjen e detajeve të credit notes.");
    } finally {
      setViewLoading(false);
    }
  };

  const rows = data.map((item, index) => ({
    id: item.TransactionID ?? `credit-note-row-${index}`,
    rowNumber: paginationModel.page * paginationModel.pageSize + index + 1,
    InvoiceNr:
      item.FullInvoiceNr ||
      (item.InvoiceNr ? `CNT-${item.FiscalYear || fiscalYear}-${item.InvoiceNr}` : ""),
    Date: formatDate(item.Date),
    AccountName: item.AccountName,
    AmountWithoutVAT: item.AmountWithoutVAT,
    VATTotal: item.VATTotal,
    Total: item.Total,
    deleteItemId: item.TransactionID,
    rowInfo: item,
  }));

  useEffect(() => {
    fetchTotals(fiscalYear, filterModel.items, quickFilter);
  }, [fiscalYear, filterModel, quickFilter]);

  React.useEffect(() => {
    fetchData();
  }, [paginationModel, sortModel, filterModel, quickFilter, fiscalYear]);

  return (
    <div className="Unit Crud__Container CNTP__page">
      <div className="CNTP__content">
        <CrudPageHeader
          icon={AiFillAppstore}
          title="Lista e Kredit Notave"
          description="Forma e Credit Notave: Menaxhoni, shtoni, editoni dhe fshini credit notat me të njëjtën përvojë si avanset."
          tutorialUrl="https://www.youtube.com/@Accountree"
          tutorialLabel="Shiko Tutorialin"
          className="CNTP__header"
          action={
            <AddElementsButton
              text="Krijo Credit Notë"
              icon="bi bi-plus-circle"
              className="button-68"
              onClick={() => history.push("/Sales/creditnote/new")}
            />
          }
        />

        <ReusableServerDataGrid
          columns={CreditNoteGridHeader}
          rows={rows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onView={handleView}
          showViewAction={true}
          showPayment={true}
          actionsMinWidth={90}
          actionsFlex={0.75}
          height={620}
          rowCount={rowCount}
          loading={loading}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          sortModel={sortModel}
          setSortModel={setSortModel}
          filterModel={filterModel}
          setFilterModel={setFilterModel}
          quickFilter={quickFilter}
          setQuickFilter={setQuickFilter}
          getExportRows={fetchExportRows}
          summaryFooter={{
            labelField: "AccountName",
            label: "Totali",
            values: {
              AmountWithoutVAT: formatCurrency(totals.AmountWithoutVAT),
              VATTotal: formatCurrency(totals.VATTotal),
              Total: formatCurrency(totals.Total),
            },
          }}
        />

        <CreditNoteViewModal
          show={showViewModal}
          onHide={() => {
            setShowViewModal(false);
            setViewData(null);
          }}
          data={viewData}
          loading={viewLoading}
        />
      </div>
    </div>
  );
}
